/**
 * Room generation system using cave-dig algorithm.
 *
 * Generates procedural room layouts with:
 * - Variable room sizes
 * - Cover placement (crates, pillars, debris)
 * - Enemy spawn points
 * - Entrance and exit positions
 *
 * Room generation follows Nuclear Throne style:
 * - Connected arenas (not corridors)
 * - Organic room shapes
 * - Strategic cover placement
 * - Difficulty-based enemy spawns
 */

/**
 * Configuration for room generation.
 */
export interface RoomConfig {
  /** Minimum room width */
  minWidth: number;
  /** Maximum room width */
  maxWidth: number;
  /** Minimum room height */
  minHeight: number;
  /** Maximum room height */
  maxHeight: number;
  /** Cover density (0-1, how much cover to place) */
  coverDensity: number;
  /** Number of enemies to spawn */
  enemyCount: number;
  /** Types of enemies that can spawn */
  enemyTypes: EnemyType[];
  /** HP multiplier for enemies (difficulty scaling) */
  hpMultiplier: number;
  /** Damage multiplier for enemies (difficulty scaling) */
  damageMultiplier: number;
}

/**
 * Enemy type identifier.
 */
export type EnemyType = 'sentry' | 'runner' | 'heavy';

/**
 * Cover type identifier.
 */
export type CoverType = 'crate' | 'pillar' | 'debris';

/**
 * Generated room layout.
 */
export interface RoomLayout {
  /** Room width */
  width: number;
  /** Room height */
  height: number;
  /** Cover positions and types */
  coverPositions: Array<{ x: number; y: number; type: CoverType }>;
  /** Enemy spawn positions and types */
  enemySpawns: Array<{ x: number; y: number; type: EnemyType }>;
  /** Boss spawn position (only for boss rooms) */
  bossSpawn?: { x: number; y: number };
  /** Entrance position */
  entrance: { x: number; y: number };
  /** Exit position */
  exit: { x: number; y: number };
  /** Whether this is a boss room */
  isBossRoom: boolean;
}

/**
 * Generate a random room layout based on configuration.
 *
 * Uses a simple cave-dig algorithm:
 * 1. Start with rectangular base
 * 2. Place entrance and exit on opposite sides
 * 3. Place cover in clusters and scattered
 * 4. Place enemies away from entrance
 *
 * @param config - Room generation configuration
 * @returns Generated room layout
 */
export function generateRoom(config: RoomConfig): RoomLayout {
  // Generate room dimensions
  const width = Math.floor(
    config.minWidth + Math.random() * (config.maxWidth - config.minWidth)
  );
  const height = Math.floor(
    config.minHeight + Math.random() * (config.maxHeight - config.minHeight)
  );

  // Place entrance and exit
  const { entrance, exit } = placeEntranceExit(width, height);

  // Place cover
  const coverPositions = placeCover(width, height, config.coverDensity, entrance, exit);

  // Place enemies
  const enemySpawns = placeEnemies(
    width,
    height,
    config.enemyCount,
    config.enemyTypes,
    entrance,
    coverPositions
  );

  return {
    width,
    height,
    coverPositions,
    enemySpawns,
    entrance,
    exit,
    isBossRoom: false,
  };
}

/**
 * Place entrance and exit on opposite sides of the room.
 *
 * @param width - Room width
 * @param height - Room height
 * @returns Entrance and exit positions
 */
function placeEntranceExit(
  width: number,
  height: number
): { entrance: { x: number; y: number }; exit: { x: number; y: number } } {
  // Choose random side for entrance (0=north, 1=east, 2=south, 3=west)
  const entranceSide = Math.floor(Math.random() * 4);

  // Place entrance on the chosen side
  const entrance = getPositionOnSide(width, height, entranceSide);

  // Place exit on opposite side
  const exitSide = (entranceSide + 2) % 4;
  const exit = getPositionOnSide(width, height, exitSide);

  return { entrance, exit };
}

/**
 * Get a position on a specific side of the room.
 *
 * Positions are inset from the wall edge by a margin to ensure
 * entities (player mech, exit door) remain accessible.
 *
 * @param width - Room width
 * @param height - Room height
 * @param side - Side index (0=north, 1=east, 2=south, 3=west)
 * @returns Position on the specified side
 */
function getPositionOnSide(
  width: number,
  height: number,
  side: number
): { x: number; y: number } {
  // Inset from wall to ensure accessibility (matches mech collision radius)
  const wallMargin = 0.5;
  const halfWidth = width / 2 - wallMargin;
  const halfHeight = height / 2 - wallMargin;

  switch (side) {
    case 0: // North (+Y)
      return { x: (Math.random() - 0.5) * width * 0.6, y: halfHeight };
    case 1: // East (+X)
      return { x: halfWidth, y: (Math.random() - 0.5) * height * 0.6 };
    case 2: // South (-Y)
      return { x: (Math.random() - 0.5) * width * 0.6, y: -halfHeight };
    case 3: // West (-X)
      return { x: -halfWidth, y: (Math.random() - 0.5) * height * 0.6 };
    default:
      return { x: 0, y: halfHeight };
  }
}

/**
 * Place cover objects in the room.
 *
 * Places cover in a mix of:
 * - Clustered groups (strategic positions)
 * - Scattered individual pieces
 *
 * Avoids placing cover on entrance/exit or too close to them.
 *
 * @param width - Room width
 * @param height - Room height
 * @param density - Cover density (0-1)
 * @param entrance - Entrance position
 * @param exit - Exit position
 * @returns Array of cover positions and types
 */
function placeCover(
  width: number,
  height: number,
  density: number,
  entrance: { x: number; y: number },
  exit: { x: number; y: number }
): Array<{ x: number; y: number; type: CoverType }> {
  const coverPositions: Array<{ x: number; y: number; type: CoverType }> = [];

  // Calculate number of cover pieces based on room size and density
  const area = width * height;
  const coverCount = Math.floor((area / 25) * density); // ~1 cover per 25 units² at density=1

  // Place cover in clusters (70% of cover)
  const clusterCount = Math.floor(coverCount * 0.7);
  const clusters = Math.ceil(clusterCount / 4); // ~4 pieces per cluster

  for (let i = 0; i < clusters; i++) {
    // Random cluster center
    const centerX = (Math.random() - 0.5) * width * 0.8;
    const centerY = (Math.random() - 0.5) * height * 0.8;

    // Skip if too close to entrance or exit
    if (
      isNearPosition(centerX, centerY, entrance, 3) ||
      isNearPosition(centerX, centerY, exit, 3)
    ) {
      continue;
    }

    // Place 3-5 cover pieces around the cluster center
    const piecesInCluster = 3 + Math.floor(Math.random() * 3);
    for (let j = 0; j < piecesInCluster; j++) {
      const offsetX = (Math.random() - 0.5) * 4;
      const offsetY = (Math.random() - 0.5) * 4;
      const x = centerX + offsetX;
      const y = centerY + offsetY;

      // Check bounds
      if (Math.abs(x) < width / 2 - 1 && Math.abs(y) < height / 2 - 1) {
        coverPositions.push({
          x,
          y,
          type: randomCoverType(),
        });
      }
    }
  }

  // Place scattered cover (30% of cover)
  const scatterCount = Math.floor(coverCount * 0.3);
  for (let i = 0; i < scatterCount; i++) {
    const x = (Math.random() - 0.5) * width * 0.9;
    const y = (Math.random() - 0.5) * height * 0.9;

    // Skip if too close to entrance, exit, or existing cover
    if (
      isNearPosition(x, y, entrance, 2.5) ||
      isNearPosition(x, y, exit, 2.5) ||
      coverPositions.some((cover) => isNearPosition(x, y, cover, 2))
    ) {
      continue;
    }

    coverPositions.push({
      x,
      y,
      type: randomCoverType(),
    });
  }

  return coverPositions;
}

/**
 * Place enemy spawns in the room.
 *
 * Enemies are placed:
 * - Away from the entrance (no spawn camping)
 * - Distributed across the room
 * - Mixed types based on config
 *
 * @param width - Room width
 * @param height - Room height
 * @param count - Number of enemies to spawn
 * @param types - Allowed enemy types
 * @param entrance - Entrance position (avoid spawning near this)
 * @param cover - Cover positions (prefer spawning near cover)
 * @returns Array of enemy spawn positions and types
 */
function placeEnemies(
  width: number,
  height: number,
  count: number,
  types: EnemyType[],
  entrance: { x: number; y: number },
  cover: Array<{ x: number; y: number; type: CoverType }>
): Array<{ x: number; y: number; type: EnemyType }> {
  const enemySpawns: Array<{ x: number; y: number; type: EnemyType }> = [];

  // Place enemies with multiple attempts to find good positions
  let attempts = 0;
  const maxAttempts = count * 10;

  while (enemySpawns.length < count && attempts < maxAttempts) {
    attempts++;

    // Random position in room
    const x = (Math.random() - 0.5) * width * 0.8;
    const y = (Math.random() - 0.5) * height * 0.8;

    // Must be far from entrance (no spawn camping)
    if (isNearPosition(x, y, entrance, 6)) {
      continue;
    }

    // Must not be too close to other enemies
    if (enemySpawns.some((enemy) => isNearPosition(x, y, enemy, 3))) {
      continue;
    }

    // Random enemy type from allowed types
    const type = types[Math.floor(Math.random() * types.length)];

    enemySpawns.push({ x, y, type });
  }

  return enemySpawns;
}

/**
 * Check if a position is near another position.
 *
 * @param x - X coordinate to check
 * @param y - Y coordinate to check
 * @param target - Target position
 * @param radius - Distance threshold
 * @returns True if within radius of target
 */
function isNearPosition(
  x: number,
  y: number,
  target: { x: number; y: number },
  radius: number
): boolean {
  const dx = x - target.x;
  const dy = y - target.y;
  return Math.sqrt(dx * dx + dy * dy) < radius;
}

/**
 * Get a random cover type.
 *
 * Distribution:
 * - 50% crates
 * - 30% pillars
 * - 20% debris
 *
 * @returns Random cover type
 */
function randomCoverType(): CoverType {
  const rand = Math.random();
  if (rand < 0.5) return 'crate';
  if (rand < 0.8) return 'pillar';
  return 'debris';
}

/**
 * Generate a boss room layout.
 *
 * Boss rooms are larger with less cover to allow for movement.
 * Boss spawns at the far end of the room.
 *
 * @param floor - Current floor number (affects difficulty)
 * @returns Boss room layout
 */
export function generateBossRoom(floor: number): RoomLayout {
  // Larger arena for boss fights
  const width = 25;
  const height = 25;

  // Place entrance and exit
  const { entrance, exit } = placeEntranceExit(width, height);

  // Less cover in boss rooms (more open arena)
  const coverPositions = placeCover(width, height, 0.05, entrance, exit);

  // Boss spawns at far side (near exit)
  const bossSpawn = {
    x: exit.x * 0.7, // 70% of the way toward exit
    y: exit.y * 0.7,
  };

  return {
    width,
    height,
    coverPositions,
    enemySpawns: [], // No regular enemies, only boss
    bossSpawn,
    entrance,
    exit,
    isBossRoom: true,
  };
}

/**
 * Get room configuration based on floor number.
 *
 * Rooms scale with floor number:
 * - Size increases
 * - More cover
 * - More enemies
 * - Harder enemy types
 *
 * @param floorNumber - Current floor number (1-indexed)
 * @returns Room configuration for this floor
 */
export function getRoomConfig(floorNumber: number): RoomConfig {
  // Clamp floor number to reasonable range
  const floor = Math.max(1, Math.min(floorNumber, 20));

  return {
    minWidth: 15 + floor * 2,
    maxWidth: 25 + floor * 3,
    minHeight: 15 + floor * 2,
    maxHeight: 25 + floor * 3,
    coverDensity: Math.min(0.1 + floor * 0.02, 0.5), // Cap at 0.5
    enemyCount: 2 + floor,
    enemyTypes: getEnemyTypesForFloor(floor),
    hpMultiplier: 1 + (floor - 1) * 0.2, // +20% HP per floor
    damageMultiplier: 1 + (floor - 1) * 0.1, // +10% damage per floor
  };
}

/**
 * Get enemy types available for a given floor.
 *
 * Enemy progression:
 * - Floor 1-2: Sentries only
 * - Floor 3-5: Sentries + Runners
 * - Floor 6+: All enemy types
 *
 * @param floor - Current floor number
 * @returns Array of enemy types for this floor
 */
function getEnemyTypesForFloor(floor: number): EnemyType[] {
  if (floor <= 2) {
    return ['sentry'];
  } else if (floor <= 5) {
    return ['sentry', 'runner'];
  } else {
    return ['sentry', 'runner', 'heavy'];
  }
}
